import { GamePhase } from './enums.types';
import type { DebuffType } from './enums.types';
import type { GameState } from './game-state.types';
import type { HeroState, EnemyState, LootDrop } from './hero.types';

export function initialGameState(): GameState {
  return { phase: GamePhase.Walking, hero: null, enemy: null, loot: null, lastVictoryLoot: null,
    tick: 0, serverTimeMs: 0, routeWaypoints: null, enemyOnScreenRight: undefined };
}
export type ServerStateEvent =
  | { type: 'init'; hero: HeroState; activity?: string }
  | { type: 'hero_snapshot'; hero: HeroState }
  | { type: 'hero_move'; x: number; y: number; preservePhase?: boolean }
  | { type: 'position_sync'; x: number; y: number; activity: string }
  | { type: 'route'; waypoints: Array<{ x: number; y: number }> }
  | { type: 'combat_start'; enemy: EnemyState; enemyOnScreenRight: boolean }
  | { type: 'attack'; heroHp: number; enemyHp: number }
  | { type: 'enemy_regen'; hp: number }
  | { type: 'combat_end' }
  | { type: 'loot'; loot: LootDrop | null }
  | { type: 'clear_loot' }
  | { type: 'died' }
  | { type: 'revived'; hp: number }
  | { type: 'town_enter' }
  | { type: 'town_exit' }
  | { type: 'hero_patch'; fields: Partial<HeroState> }
  | { type: 'hero_hp'; hp: number }
  | { type: 'debuff'; debuffType: DebuffType; durationMs: number; expiresAtMs?: number; nowMs: number };

function serverPhase(hero: HeroState, activity = hero.serverActivityState): GamePhase {
  if (hero.hp <= 0 || activity?.toLowerCase() === 'dead') return GamePhase.Dead;
  if (activity?.toLowerCase() === 'resting') return GamePhase.Resting;
  if (activity?.toLowerCase() === 'in_town') return GamePhase.InTown;
  return GamePhase.Walking;
}
/**
 * Project server snapshots/events into client state without mutating prior state or payloads.
 * No clocks, random outcomes, transport, DOM/Pixi, XP awards or quota simulation.
 * The caller supplies time for presentation debuff expiry and keeps position interpolation separate.
 */
export function applyServerStateEvent(state: Readonly<GameState>, event: ServerStateEvent): GameState {
  switch (event.type) {
    case 'init': {
      const hero = structuredClone(event.hero);
      return { ...initialGameState(), hero, phase: serverPhase(hero, event.activity) };
    }
    case 'hero_snapshot': {
      const hero = structuredClone(event.hero);
      const dead = hero.hp <= 0 || hero.serverActivityState?.toLowerCase() === 'dead';
      let phase = state.phase;
      const activity = hero.serverActivityState?.toLowerCase();
      if (dead) phase = GamePhase.Dead;
      else if (state.phase !== GamePhase.Fighting && ['walking', 'resting', 'in_town'].includes(activity ?? '')) {
        phase = serverPhase(hero);
      }
      return { ...state, hero, phase, ...(dead ? { enemy: null, enemyOnScreenRight: undefined } : {}) };
    }
    case 'hero_move': {
      const activity = state.hero?.serverActivityState?.toLowerCase();
      const leaveRest = !event.preservePhase && state.phase === GamePhase.Resting &&
        activity !== 'resting' && activity !== 'in_town';
      return { ...state, phase: leaveRest ? GamePhase.Walking : state.phase,
        hero: state.hero ? { ...state.hero, position: { x: event.x, y: event.y } } : null };
    }
    case 'position_sync':
      return { ...state, hero: state.hero ? { ...state.hero, position: { x: event.x, y: event.y } } : null,
        phase: event.activity === 'walking' && state.phase !== GamePhase.Fighting && state.phase !== GamePhase.Dead
          ? GamePhase.Walking : state.phase };
    case 'route': {
      const hp = state.hero?.hp ?? 1;
      const activity = state.hero?.serverActivityState?.toLowerCase();
      const phase = hp <= 0 ? GamePhase.Dead : activity === 'in_town' ? GamePhase.InTown :
        activity === 'resting' ? GamePhase.Resting : GamePhase.Walking;
      return { ...state, phase,
        routeWaypoints: event.waypoints.length >= 2 ? event.waypoints.map((point) => ({ ...point })) : null };
    }
    case 'combat_start':
      return { ...state, phase: GamePhase.Fighting, enemy: structuredClone(event.enemy),
        loot: null, enemyOnScreenRight: event.enemyOnScreenRight };
    case 'attack':
      return { ...state, hero: state.hero ? { ...state.hero, hp: event.heroHp } : null,
        enemy: state.enemy ? { ...state.enemy, hp: event.enemyHp } : null };
    case 'enemy_regen':
      return state.enemy ? { ...state, enemy: { ...state.enemy, hp: event.hp } } : { ...state };
    case 'combat_end':
      return { ...state, phase: (state.hero?.hp ?? 0) <= 0 ? GamePhase.Dead : GamePhase.Walking,
        enemy: null, enemyOnScreenRight: undefined };
    case 'loot': {
      const loot = event.loot ? structuredClone(event.loot) : null;
      return { ...state, loot, lastVictoryLoot: loot ?? state.lastVictoryLoot };
    }
    case 'clear_loot':
      return { ...state, loot: null };
    case 'died':
      return { ...state, phase: GamePhase.Dead };
    case 'revived':
      return { ...state, hero: state.hero ? { ...state.hero, hp: event.hp } : null,
        phase: GamePhase.Walking, enemy: null, enemyOnScreenRight: undefined };
    case 'town_enter':
      return { ...state, phase: GamePhase.InTown };
    case 'town_exit':
      return { ...state, phase: GamePhase.Walking };
    case 'hero_patch':
      return { ...state, hero: state.hero ? { ...state.hero, ...structuredClone(event.fields) } : null };
    case 'hero_hp':
      return { ...state, hero: state.hero ? { ...state.hero, hp: Math.min(event.hp, state.hero.maxHp) } : null };
    case 'debuff': {
      if (!state.hero) return { ...state };
      const expiresAtMs = Number.isFinite(event.expiresAtMs) ? event.expiresAtMs! : event.nowMs + Math.max(0, event.durationMs);
      const debuffs = state.hero.debuffs.filter((debuff) => debuff.type !== event.debuffType &&
        (debuff.expiresAtMs ?? event.nowMs + debuff.remainingMs) > event.nowMs && debuff.remainingMs > 0);
      return { ...state, hero: { ...state.hero, debuffs: [...debuffs, {
        type: event.debuffType, remainingMs: Math.max(0, expiresAtMs - event.nowMs),
        durationMs: Math.max(0, event.durationMs), expiresAtMs,
      }] } };
    }
  }
}
